import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BellRing, Loader2 } from 'lucide-react';
import { api, useAuthStore } from '../../lib/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { useTranslation, useI18nStore } from '../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStopAdmin.${k}`, p);


const Row = ({ title, desc, children }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b last:border-0 border-slate-100 dark:border-slate-800">
    <div><div className="text-sm font-semibold">{title}</div>{desc && <div className="text-xs text-slate-500">{desc}</div>}</div>
    {children}
  </div>
);

export function ReminderSettingsButton() {
  useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) api.get('/planning/start-stop/reminder-settings').then((r) => setCfg(r.data.data)).catch(() => toast.error(tt('remLoadFailed')));
  }, [open]);
  if (!['SUPER_ADMIN', 'FULL_ADMIN'].includes(role)) return null;
  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/planning/start-stop/reminder-settings', { ...cfg, hours_after_start: Number(cfg.hours_after_start), repeat_after_min: Number(cfg.repeat_after_min) });
      toast.success(tt('remSaved')); setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? tt('remInvalid') : d || tt('saveFailed'));
    } finally { setSaving(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="h-9 px-3 rounded-lg border text-sm font-semibold flex items-center gap-1" data-testid="ss-reminder-settings-btn"><BellRing className="h-4 w-4" />{tt('remBtn')}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ss-reminder-dialog">
          <DialogHeader>
            <DialogTitle>{tt('remTitle')}</DialogTitle>
            <DialogDescription>{tt('remDesc')}</DialogDescription>
          </DialogHeader>
          {!cfg ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6 text-slate-400" /> : (
            <div>
              <Row title={tt('remStartPrompts')} desc={tt('remStartPromptsDesc')}><Switch checked={!!cfg.start_prompts_enabled} onCheckedChange={set('start_prompts_enabled')} data-testid="ss-rem-start-prompts" /></Row>
              <Row title={tt('remClose')} desc={tt('remCloseDesc')}><Switch checked={!!cfg.close_reminders_enabled} onCheckedChange={set('close_reminders_enabled')} data-testid="ss-rem-close" /></Row>
              <Row title={tt('remEnabled')}><Switch checked={cfg.enabled} onCheckedChange={set('enabled')} data-testid="ss-rem-enabled" /></Row>
              <div className={cfg.enabled ? '' : 'opacity-40 pointer-events-none'}>
                <Row title={tt('remHours')} desc={tt('remHoursDesc')}>
                  <div className="flex items-center gap-1"><Input type="number" min={1} max={24} step={0.5} value={cfg.hours_after_start} onChange={(e) => set('hours_after_start')(e.target.value)} className="h-9 w-20" data-testid="ss-rem-hours" /><span className="text-xs text-slate-500">{tt('hours')}</span></div>
                </Row>
                <Row title={tt('remRepeat')} desc={tt('remRepeatDesc')}>
                  <Switch checked={cfg.repeat_enabled} onCheckedChange={set('repeat_enabled')} data-testid="ss-rem-repeat" />
                </Row>
                {cfg.repeat_enabled && (
                  <Row title={tt('remSecond')} desc={tt('remSecondDesc')}>
                    <div className="flex items-center gap-1"><Input type="number" min={15} max={360} step={15} value={cfg.repeat_after_min} onChange={(e) => set('repeat_after_min')(e.target.value)} className="h-9 w-20" data-testid="ss-rem-repeat-min" /><span className="text-xs text-slate-500">{tt('min')}</span></div>
                  </Row>
                )}
                <Row title={tt('remAdmins')} desc={tt('remAdminsDesc')}>
                  <Switch checked={cfg.notify_admins} onCheckedChange={set('notify_admins')} data-testid="ss-rem-admins" />
                </Row>
              </div>
              <p className="text-[11px] text-slate-400 pt-2">{tt('remEvery')}</p>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => setOpen(false)} className="h-9 px-3 rounded-lg text-sm">{tt('cancel')}</button>
                <button type="button" onClick={save} disabled={saving} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-50" data-testid="ss-rem-save">{saving ? tt('saving') : tt('save')}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
